import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "command-naming-guidelines"
    }}>{`Command naming guidelines`}</h1>
    <p>{`As an extension developer, you can now create and distribute your own commands for Adobe Commerce and Magento Open Source applications. But as for any implementation, it's also important to follow some general conventions to keep your commands consistent with commands from other developers. Being consistent in this way reduces the user's learning curve.`}</p>
    <p>{`This topic discusses our recommended naming conventions.`}</p>
    <h2 {...{
      "id": "name"
    }}>{`Name`}</h2>
    <p>{`A command `}<em parentName="p">{`name`}</em>{` is a part of the command, which defines behavior of the command on the very high level. In the command it goes right after the command's name.
For example, in `}<inlineCode parentName="p">{`bin/magento setup:upgrade`}</inlineCode>{`, `}<inlineCode parentName="p">{`bin/magento`}</inlineCode>{` is the command's name and `}<inlineCode parentName="p">{`setup:upgrade`}</inlineCode>{` is the name of the command.`}</p>
    <p>{`If you have an application installation handy, enter the following to display the current list of commands:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento list
`}</code></pre>
    <h3 {...{
      "id": "format"
    }}>{`Format`}</h3>
    <p>{`The format for command names is `}<inlineCode parentName="p">{`group:[subject:]action`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "group"
    }}>{`group`}</h4>
    <p><inlineCode parentName="p">{`group`}</inlineCode>{` represents a group of related commands. Commands in a group display in a list, which in turn makes it easier for the user to find the desired command. To find a group name for a command, imagine an subject area where it can be used. The subject area can be any of the following:`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`Domain`}</em>{` area (for example, `}<inlineCode parentName="li">{`module`}</inlineCode>{` for actions with modules, `}<inlineCode parentName="li">{`info`}</inlineCode>{` for commands that provide some information)`}</li>
      <li parentName="ul"><em parentName="li">{`Workflow`}</em>{` area (for example, `}<inlineCode parentName="li">{`admin`}</inlineCode>{` for commands that can be used by an administrator, `}<inlineCode parentName="li">{`dev`}</inlineCode>{` for a developer)`}</li>
    </ul>
    <h4 {...{
      "id": "subject"
    }}>{`subject`}</h4>
    <p><inlineCode parentName="p">{`subject`}</inlineCode>{` is a subject for the action. The subject is optional, but it can be useful for defining sets of commands that work with the same object. If a subject is represented by a compound word, use a dash or hyphen character to separate the words.`}</p>
    <h4 {...{
      "id": "action"
    }}>{`action`}</h4>
    <p><inlineCode parentName="p">{`action`}</inlineCode>{` is an action the command does.`}</p>
    <h4 {...{
      "id": "examples"
    }}>{`Examples`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-terminal"
      }}>{`// general commands: just a group and an action
bin/magento setup:install
bin/magento module:status

// set of commands with a subject
bin/magento setup:config:set
bin/magento setup:config:delete
bin/magento setup:db-schema:upgrade
bin/magento setup:db-data:upgrade
`}</code></pre>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
`db-schema` and `db-data` are examples of compound words.
    <h2 {...{
      "id": "options-and-arguments"
    }}>{`Options and arguments`}</h2>
    <p>{`Options and arguments follow the command name and modify the command's behavior.`}</p>
    <p>{`For example, in `}<inlineCode parentName="p">{`bin/magento module:disable --force Magento_Catalog`}</inlineCode>{`, the `}<inlineCode parentName="p">{`--force`}</inlineCode>{` `}<em parentName="p">{`option`}</em>{` and the `}<inlineCode parentName="p">{`Magento_Catalog`}</inlineCode>{` `}<em parentName="p">{`argument`}</em>{` bypass the restrictions and specify a particular module to be disabled; in this case, regardless of dependencies on other modules.`}</p>
    <p>{`Options and arguments create different user experiences. As a developer, you can choose which type of input is better for your particular case.`}</p>
    <h3 {...{
      "id": "arguments"
    }}>{`Arguments`}</h3>
    <p>{`Arguments are values passed by the user in a specified order. The argument name is not visible to the user.`}</p>
    <h4 {...{
      "id": "format-1"
    }}>{`Format`}</h4>
    <p>{`The format for command arguments is a single word or a compound word separated with a dash or hyphen character.`}</p>
    <h4 {...{
      "id": "examples-1"
    }}>{`Examples`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{` bin/magento dev:theme:create frontend vendor themename
`}</code></pre>
    <p>{`where:`}</p>
    <p><inlineCode parentName="p">{`frontend`}</inlineCode>{` is a subject area argument`}</p>
    <p><inlineCode parentName="p">{`vendor`}</inlineCode>{` is a vendor argument`}</p>
    <p><inlineCode parentName="p">{`themename`}</inlineCode>{` is a theme name argument`}</p>
    <p>{`Use arguments when you need required data from the user. We recommend as few arguments as possible (no more then three) so the user will not confuse their order.`}</p>
    <p>{`To make it simpler for the user, we recommend the following:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Run the CLI multiple times for providing multiple similar values instead of running it once with 20 values`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Use default values for required arguments where possible.`}</p>
        <p parentName="li">{`You can then use options instead of arguments to minimize the amount of required data the user must enter.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Replace arguments with options: options are named, so the user can provide them in any order. This requires additional data validation (by default, all options are optional).`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "options"
    }}>{`Options`}</h3>
    <p>{`Options are name-value pairs. The sequence of entered values doesn't matter.`}</p>
    <p>{`An option can have a value or no value. An option that does not require a value represents a flag (`}<inlineCode parentName="p">{`yes`}</inlineCode>{` or `}<inlineCode parentName="p">{`no`}</inlineCode>{`).`}</p>
    <p>{`An option can also have a one-letter shortcut as an alternative to its full name. Enable shortcuts for often-used options or if it's easy to determine what the shortcut means. Usually it makes sense to enable shortcuts for options similar to the ones used in widely-used commands (for example, `}<inlineCode parentName="p">{`-f`}</inlineCode>{` for `}<inlineCode parentName="p">{`--force`}</inlineCode>{`, `}<inlineCode parentName="p">{`-v`}</inlineCode>{` for `}<inlineCode parentName="p">{`--verbose`}</inlineCode>{`, `}<inlineCode parentName="p">{`-h`}</inlineCode>{` for `}<inlineCode parentName="p">{`--help`}</inlineCode>{`).`}</p>
    <h4 {...{
      "id": "format-2"
    }}>{`Format`}</h4>
    <p>{`The format for command options is a single word or a compound word separated with a dash or hyphen character.`}</p>
    <h4 {...{
      "id": "examples-2"
    }}>{`Examples`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento dev:theme:create --parent=Magento/luma frontend arg1 arg2
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento dev:theme:create -p=Magento/luma frontend vendor themename
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento dev:theme:create --extend-from=Magento/luma frontend vendor themename
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento module:disable -f Magento_Cms
`}</code></pre>
    <p>{`Where:`}</p>
    <p><inlineCode parentName="p">{`--parent`}</inlineCode>{` is an option that specifies a parent theme`}</p>
    <p><inlineCode parentName="p">{`-p`}</inlineCode>{` is a shortcut for `}<inlineCode parentName="p">{`--parent`}</inlineCode></p>
    <p><inlineCode parentName="p">{`-f`}</inlineCode>{` is a shortcut for a non-value option `}<inlineCode parentName="p">{`--force`}</inlineCode></p>
    <p><inlineCode parentName="p">{`arg1`}</inlineCode>{`, `}<inlineCode parentName="p">{`arg2`}</inlineCode>{`, `}<inlineCode parentName="p">{`frontend`}</inlineCode>{`, `}<inlineCode parentName="p">{`vendor`}</inlineCode>{` and `}<inlineCode parentName="p">{`themename`}</inlineCode>{` are arguments (see `}<a parentName="p" {...{
        "href": "#options-and-arguments"
      }}>{`Command options and arguments`}</a>{`).`}</p>
    <p>{`Use options for:`}</p>
    <ul>
      <li parentName="ul">{`Optional data`}</li>
      <li parentName="ul">{`Required data that has a default value`}</li>
    </ul>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-terminal"
      }}>{`// correct
bin/magento dev:theme:create --extend-from=Magento/luma frontend Foo bar
bin/magento module:disable --force Magento_Catalog
bin/magento module:disable -f Magento_Catalog

//incorrect
bin/magento module:disable --force=1 Magento_Catalog
bin/magento module:disable -f=yes Magento_Catalog
`}</code></pre>
    <h2 {...{
      "id": "recommendations"
    }}>{`Recommendations`}</h2>
    <p>{`To avoid naming your command the same as another command, we recommend:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Looking at other extensions in the Commerce Marketplace before you choose a name for your commands. By planning ahead, you can avoid naming collisions entirely.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Restricting command names to start with a unique name, such as a vendor name. The usability of the command depends on what you choose for a vendor name.`}</p>
        <p parentName="li">{`For example, `}<inlineCode parentName="p">{`myname:dev:theme:create`}</inlineCode>{` is not obvious and is hard to remember.`}</p>
        <p parentName="li">{`The vendor name doesn't have to start the command name; it could be in the middle. This way, related commands are grouped together.`}</p>
        <p parentName="li">{`Examples:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-terminal"
          }}>{`dev:myname:theme:create
dev:myname:theme:delete
`}</code></pre>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      